import { RESUME, PROFILE } from "../data/content";
import { GraduationCap, Briefcase, Wrench, Trophy, Download, Award } from "lucide-react";

/* ── Section header ── */
function SectionTitle({ icon: Icon, title }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 20, paddingBottom: 12, borderBottom: "1px solid #E4E4E7" }}>
      <div style={{ width: 28, height: 28, borderRadius: 8, background: "#09090B", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        <Icon size={14} color="#fff" />
      </div>
      <h2 style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 15, color: "#09090B", margin: 0, letterSpacing: "-0.01em", textTransform: "uppercase" }}>
        {title}
      </h2>
    </div>
  );
}

/* ── Timeline item ── */
function TimelineItem({ title, sub, period, note, last }) {
  return (
    <div style={{ display: "flex", gap: 14, paddingBottom: last ? 0 : 20 }}>
      {/* Dot + line */}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: "#2563EB", border: "2px solid #fff", boxShadow: "0 0 0 2px #2563EB", marginTop: 4, flexShrink: 0 }} />
        {!last && <div style={{ width: 1, flex: 1, background: "#E4E4E7", marginTop: 6 }} />}
      </div>
      {/* Content */}
      <div style={{ paddingBottom: last ? 0 : 4 }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 700, fontSize: 13, color: "#09090B", lineHeight: 1.3 }}>{title}</div>
        {sub && <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#64748B", marginTop: 2 }}>{sub}</div>}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 10, fontWeight: 700, background: "#EFF6FF", color: "#2563EB", padding: "2px 7px", borderRadius: 4 }}>
            {period}
          </span>
          {note && <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 10, color: "#94A3B8" }}>{note}</span>}
        </div>
      </div>
    </div>
  );
}

export default function Resume() {
  return (
    <div style={{ maxWidth: 1100, margin: "0 auto", padding: "48px 24px 80px" }}>

      {/* Header */}
      <div className="reveal" style={{ marginBottom: 40 }}>
        <span className="section-label">CV</span>
        <h1 style={{
          fontFamily: "'Archivo',sans-serif", fontWeight: 900,
          fontSize: "clamp(28px,4vw,52px)", color: "#09090B",
          margin: "0 0 10px", letterSpacing: "-0.03em", lineHeight: 1.05,
        }}>
          Curriculum<br /><span style={{ color: "#2563EB" }}>Vitae</span>
        </h1>
        <p style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 15, color: "#64748B", margin: 0, maxWidth: 460, lineHeight: 1.65 }}>
          "Biến kiến thức thành thứ chạm được."
        </p>
      </div>

      {/* Profile card */}
      <div className="reveal reveal-delay-1 resume-profile-card">
        {/* Left: avatar */}
        <div className="resume-avatar">
          {PROFILE.avatar ? (
            <img src={PROFILE.avatar} alt={PROFILE.realName} style={{ width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 15%" }} />
          ) : (
            <div style={{ fontSize: 52 }}>🤖</div>
          )}
        </div>
        {/* Right: info */}
        <div className="resume-info">
          <div>
            <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 800, fontSize: 22, color: "#09090B", letterSpacing: "-0.02em", marginBottom: 4 }}>
              {PROFILE.realName}
            </div>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, fontWeight: 600, color: "#2563EB", marginBottom: 10, letterSpacing: "0.04em", textTransform: "uppercase" }}>
              {PROFILE.tagline}
            </div>
            <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 13, color: "#64748B", lineHeight: 1.65, maxWidth: 500 }}>
              {PROFILE.intro}
            </div>
          </div>
          <a
            href="https://drive.google.com/file/d/1mwezL-gxYKHt0KPDfwFure85or4ZAVnz/view?usp=sharing"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              padding: "9px 18px", borderRadius: 10, width: "fit-content",
              background: "#09090B", color: "#fff",
              fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, fontWeight: 700,
              textDecoration: "none",
              transition: "background 180ms",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = "#2563EB"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#09090B"; }}
          >
            <Download size={13} /> Tải CV PDF
          </a>
        </div>
      </div>

      {/* 2 columns */}
      <div className="resume-grid">

        {/* LEFT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

          {/* Education */}
          <div className="reveal" style={{ background: "#fff", border: "1px solid #E4E4E7", borderRadius: 14, padding: "22px 20px" }}>
            <SectionTitle icon={GraduationCap} title="Học vấn" />
            {RESUME.education.map((edu, i) => (
              <TimelineItem
                key={i}
                title={edu.school}
                sub={edu.degree}
                period={edu.period}
                note={edu.note}
                last={i === RESUME.education.length - 1}
              />
            ))}
          </div>

          {/* Certificates */}
          <div className="reveal" style={{ background: "#fff", border: "1px solid #E4E4E7", borderRadius: 14, padding: "22px 20px" }}>
            <SectionTitle icon={Award} title="Chứng chỉ" />
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {RESUME.certificates.map((cert, i) => (
                <div key={i} style={{
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  padding: "10px 14px", background: "#F8FAFC",
                  border: "1px solid #E4E4E7", borderRadius: 10, flexWrap: "wrap", gap: 6,
                }}>
                  <div>
                    <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 700, fontSize: 12, color: "#09090B" }}>{cert.name}</div>
                    <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 11, color: "#64748B", marginTop: 2 }}>{cert.issuer}</div>
                  </div>
                  <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 10, fontWeight: 700, background: "#EFF6FF", color: "#2563EB", padding: "2px 7px", borderRadius: 4 }}>
                    {cert.date}
                  </span>
                </div>
              ))}
            </div>
            <a
              href="https://drive.google.com/file/d/1LHVQu6uU442IFrO0AD6VZb68fwgb4Wgu/view?usp=sharing"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                marginTop: 12, padding: "10px",
                border: "1.5px dashed #D4D4D8", borderRadius: 10,
                fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, fontWeight: 700, color: "#64748B",
                textDecoration: "none", transition: "border-color 180ms, color 180ms",
              }}
              onMouseEnter={e => { e.currentTarget.style.borderColor = "#09090B"; e.currentTarget.style.color = "#09090B"; }}
              onMouseLeave={e => { e.currentTarget.style.borderColor = "#D4D4D8"; e.currentTarget.style.color = "#64748B"; }}
            >
              Xem thêm chứng chỉ →
            </a>
          </div>

          {/* Why hire me */}
          <div className="reveal" style={{ background: "#fff", border: "1px solid #E4E4E7", borderRadius: 14, padding: "22px 20px" }}>
            <SectionTitle icon={Trophy} title="7 điểm nổi bật" />
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {RESUME.whyHireMe.map((item, i) => (
                <div key={i} style={{ display: "flex", gap: 12 }}>
                  <span style={{
                    fontFamily: "'Space Grotesk',sans-serif", fontSize: 10, fontWeight: 800,
                    color: "#2563EB", background: "#EFF6FF",
                    padding: "3px 7px", borderRadius: 6, flexShrink: 0, height: "fit-content", marginTop: 1,
                  }}>
                    {item.emoji}
                  </span>
                  <div>
                    <div style={{ fontFamily: "'Archivo',sans-serif", fontWeight: 700, fontSize: 12, color: "#09090B", marginBottom: 2 }}>{item.title}</div>
                    <div style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 11, color: "#64748B", lineHeight: 1.55 }}>{item.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div style={{ display: "flex", flexDirection: "column", gap: 28 }}>

          {/* Experience */}
          <div className="reveal" style={{ background: "#fff", border: "1px solid #E4E4E7", borderRadius: 14, padding: "22px 20px" }}>
            <SectionTitle icon={Briefcase} title="Kinh nghiệm" />
            {RESUME.experience.map((exp, i) => (
              <TimelineItem
                key={i}
                title={exp.role}
                sub={exp.org}
                period={exp.period}
                note={exp.desc && exp.desc.trim() ? exp.desc.split("\n")[0] : null}
                last={i === RESUME.experience.length - 1}
              />
            ))}
          </div>

          {/* Skills */}
          <div className="reveal" style={{ background: "#fff", border: "1px solid #E4E4E7", borderRadius: 14, padding: "22px 20px" }}>
            <SectionTitle icon={Wrench} title="Kỹ năng" />
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {RESUME.skills.map((skill, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                  <div style={{ width: 4, height: 4, borderRadius: "50%", background: "#2563EB", flexShrink: 0, marginTop: 7 }} />
                  <span style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 12, color: "#3F3F46", lineHeight: 1.55 }}>{skill}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Certificates */}
          {/* Tools */}
          <div className="reveal" style={{ background: "#fff", border: "1px solid #E4E4E7", borderRadius: 14, padding: "22px 20px" }}>
            <SectionTitle icon={Wrench} title="Công cụ" />
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {RESUME.tools.map(tool => (
                <div key={tool.name} style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  padding: "5px 10px", background: "#F8FAFC",
                  border: "1px solid #E4E4E7", borderRadius: 8,
                  fontFamily: "'Space Grotesk',sans-serif", fontSize: 11, color: "#3F3F46", fontWeight: 600,
                  transition: "border-color 150ms, background 150ms",
                  cursor: "default",
                }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "#09090B"; e.currentTarget.style.background = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "#E4E4E7"; e.currentTarget.style.background = "#F8FAFC"; }}
                >
                  <img src={tool.icon} alt={tool.name} style={{ width: 14, height: 14, borderRadius: 3 }} />
                  {tool.name}
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
